import { useEffect, useState } from 'react';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { ORBIT_DETAIL } from '../../../../../routes/paths';
import { asyncGetOrbitCasesByDocument } from '../../../../../services/orbit.service';
import { OrbitHistoryFilter } from '../types';
import { OrbitHistoryListActions } from './actions';
import {
    mapOrbitCaseToHistoryRow,
    OrbitHistoryTableRow,
} from './mapOrbitCaseToHistoryRow';

export const useOrbitHistoryList = (filters?: OrbitHistoryFilter) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<OrbitHistoryTableRow[]>([]);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);
    const [finalFilters, setFinalFilters] = useState<OrbitHistoryFilter>({});

    const paginator = (model: GridPaginationModel) => {
        if (model.page !== page) {
            setPage(model.page);
        } else if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
        }
    };

    const columns: GridColDef<OrbitHistoryTableRow>[] = [
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 90,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <OrbitHistoryListActions orbitCaseId={params.row.id} />
            ),
        },
        {
            field: 'case_number',
            headerName: 'Número de caso',
            minWidth: 120,
            flex: 0.4,
        },
        {
            field: 'citizen_document',
            headerName: 'Documento',
            minWidth: 120,
            flex: 0.4,
        },
        {
            field: 'person_type',
            headerName: 'Tipo de persona',
            minWidth: 140,
            flex: 0.45,
        },
        {
            field: 'citizen_name',
            headerName: 'Nombre',
            minWidth: 150,
            flex: 0.6,
        },
        {
            field: 'case_type_name',
            headerName: 'Tipo de caso',
            minWidth: 150,
            flex: 0.6,
        },
        {
            field: 'created_at',
            headerName: 'Fecha de creación',
            minWidth: 200,
            flex: 0.55,
        },
        {
            field: 'status_name',
            headerName: 'Estado',
            minWidth: 150,
            flex: 0.6,
        },
        {
            field: 'description',
            headerName: 'Descripción',
            minWidth: 150,
            flex: 0.6,
        },
        {
            field: 'resolution',
            headerName: 'Observación cierre',
            minWidth: 150,
            flex: 0.6,
        },
        {
            field: 'preferred_contact_method',
            headerName: 'Medio respuesta',
            minWidth: 150,
            flex: 0.6,
        },
        {
            field: 'citizen_email',
            headerName: 'Correo',
            minWidth: 150,
            flex: 0.6,
        },
        {
            field: 'citizen_phone',
            headerName: 'Teléfono',
            minWidth: 150,
            flex: 0.6,
        },
    ];

    useEffect(() => {
        setPage(0);
        setFinalFilters(filters || {});
    }, [filters]);

    useEffect(() => {
        if (filters === undefined) {
            setRows([]);
            setTotal(0);
            return;
        }

        const document = finalFilters.document?.trim();
        if (!document) {
            setRows([]);
            setTotal(0);
            return;
        }

        setLoading(true);
        asyncGetOrbitCasesByDocument(document, page + 1, pageSize)
            .then((res) => {
                setRows(
                    res.items.map((item) =>
                        mapOrbitCaseToHistoryRow(item, document)
                    )
                );
                setTotal(res.length);
            })
            .finally(() => setLoading(false));
    }, [page, pageSize, finalFilters, filters]);

    const onRowClick = (params: { row: OrbitHistoryTableRow }) => {
        if (!params.row.id) return;
        navigate(`${ORBIT_DETAIL}/${encodeURIComponent(params.row.id)}`);
    };

    return {
        columns,
        rows,
        page,
        pageSize,
        paginator,
        loading,
        total,
        onRowClick,
    };
};
